import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import LandscapeIcon from '@mui/icons-material/Landscape';

import type {
  AdminSubscriptionProject,
  AdminUserSubscriptions,
  NotificationChannel,
} from '../../api/admin';

export const KIND_META: Record<string, { label: string; icon: React.ReactNode }> = {
  hotspot: {
    label: 'Hotspots',
    icon: <LocalFireDepartmentIcon sx={{ fontSize: 18, color: '#f97316' }} />,
  },
  lightning: {
    label: 'Lightning',
    icon: <BoltIcon sx={{ fontSize: 18, color: '#a78bfa' }} />,
  },
  hazard: {
    label: 'Incidents',
    icon: <WarningAmberIcon sx={{ fontSize: 18, color: '#f59e0b' }} />,
  },
  ignition: {
    label: 'Ignition points',
    icon: <WhatshotIcon sx={{ fontSize: 18, color: '#ff2d95' }} />,
  },
  burnt_area: {
    label: 'Burnt areas',
    icon: <LandscapeIcon sx={{ fontSize: 18, color: '#b91c1c' }} />,
  },
};

export const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  email: 'Email',
  sms: 'SMS',
};

export interface SubscriptionsTableProps {
  data: AdminUserSubscriptions | undefined;
  isLoading: boolean;
  error: unknown;
  /** Whether a phone number is on the account — SMS cannot be switched on
   *  without one. */
  hasPhone: boolean;
  /** Applies one project's patch; call onSettled when the request finishes. */
  onToggle: (
    projectId: number,
    subscriptions: Record<
      string,
      { channels?: Partial<Record<NotificationChannel, boolean>> }
    >,
    onSettled: () => void
  ) => void;
  /** 'you' for one's own alerts, anything else for someone else's. */
  subject?: 'you' | 'other';
}

/**
 * Projects x detection kinds x channels, one switch per cell.
 *
 * Shared by My Account (the user's own alerts) and the admin user page (a
 * tenant admin managing someone else's) — the two differ only in whose
 * subscriptions are loaded, so the grid itself must not drift apart.
 */
export default function SubscriptionsTable({
  data,
  isLoading,
  error,
  hasPhone,
  onToggle,
  subject = 'you',
}: SubscriptionsTableProps) {
  const [pendingCell, setPendingCell] = useState<string | null>(null);
  const isSelf = subject === 'you';

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (error || !data) {
    return <Alert severity="error">Could not load notification subscriptions.</Alert>;
  }
  if (data.projects.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        There are no projects in this organisation yet.
      </Typography>
    );
  }

  // Only kinds the organisation can actually use get a column: a column of
  // permanently disabled switches explains nothing anyone here can act on.
  const kinds = (data.kinds ?? []).filter(
    (kind) => KIND_META[kind] && data.available_kinds[kind] !== false
  );
  const channels = data.channels ?? [];

  if (kinds.length === 0) {
    return (
      <Alert severity="info">
        No realtime detection types are enabled for your organisation yet, so
        there is nothing to subscribe to. Contact FiSci to have them switched on.
      </Alert>
    );
  }

  const cellId = (projectId: number, kind: string, channel: string) =>
    `${projectId}:${kind}:${channel}`;

  const toggle = (
    project: AdminSubscriptionProject,
    kind: string,
    channel: NotificationChannel,
    value: boolean
  ) => {
    const cell = cellId(project.id, kind, channel);
    setPendingCell(cell);
    onToggle(project.id, { [kind]: { channels: { [channel]: value } } }, () =>
      setPendingCell(null)
    );
  };

  const rowIsFullyOn = (project: AdminSubscriptionProject) =>
    kinds.every((kind) => project.subscriptions[kind]?.channels?.email === true);

  // Row-level subscribe/unsubscribe: email only when switching on (SMS costs
  // money per message and needs a number), both channels when switching off.
  const toggleRow = (project: AdminSubscriptionProject) => {
    const on = !rowIsFullyOn(project);
    const patch = Object.fromEntries(
      kinds.map((kind) => [
        kind,
        { channels: on ? { email: true } : { email: false, sms: false } },
      ])
    );
    setPendingCell(`${project.id}:row`);
    onToggle(project.id, patch, () => setPendingCell(null));
  };

  return (
    <TableContainer sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      {/* One header row (kind icon + channel) rather than grouped rows:
          stickyHeader pins every header cell to the same top edge, which a
          two-row header would overlap. */}
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Project</TableCell>
            {kinds.map((kind) =>
              channels.map((channel, index) => (
                <TableCell
                  key={`${kind}:${channel}`}
                  align="center"
                  sx={{
                    ...(index === 0 && {
                      borderLeft: '1px solid',
                      borderLeftColor: 'divider',
                    }),
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.25,
                    }}
                  >
                    {KIND_META[kind].icon}
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                      {CHANNEL_LABELS[channel]}
                    </Typography>
                  </Box>
                </TableCell>
              ))
            )}
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              All
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.projects.map((project) => {
            const snoozedKinds = kinds.filter(
              (kind) => project.subscriptions[kind]?.snoozed_until
            );
            const fullyOn = rowIsFullyOn(project);
            return (
              <TableRow key={project.id} hover>
                <TableCell>
                  {project.name}
                  {snoozedKinds.length > 0 && (
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                    >
                      {snoozedKinds
                        .map((kind) => KIND_META[kind].label.toLowerCase())
                        .join(', ')}{' '}
                      snoozed{isSelf ? '' : ' by the user'}
                    </Typography>
                  )}
                </TableCell>
                {kinds.map((kind) =>
                  channels.map((channel, index) => {
                    const cell = cellId(project.id, kind, channel);
                    const checked = Boolean(
                      project.subscriptions[kind]?.channels?.[channel]
                    );
                    // Switching OFF stays possible even where the gate would
                    // block switching on (mirrors the backend).
                    const disabled =
                      pendingCell === cell ||
                      pendingCell === `${project.id}:row` ||
                      (!checked && channel === 'sms' && !hasPhone);
                    const title =
                      !checked && channel === 'sms' && !hasPhone
                        ? isSelf
                          ? 'Add a mobile number above to enable SMS.'
                          : 'No mobile number on this account, so SMS cannot be delivered — add one on the left.'
                        : '';
                    return (
                      <TableCell
                        key={cell}
                        align="center"
                        sx={{
                          ...(index === 0 && {
                            borderLeft: '1px solid',
                            borderLeftColor: 'divider',
                          }),
                        }}
                      >
                        <Tooltip title={title} arrow>
                          {/* span so the tooltip works while disabled */}
                          <span>
                            <Switch
                              size="small"
                              color="secondary"
                              checked={checked}
                              disabled={disabled}
                              onChange={(_, value) =>
                                toggle(project, kind, channel, value)
                              }
                              inputProps={{
                                'aria-label': `${project.name}: ${KIND_META[kind].label} ${CHANNEL_LABELS[channel]}`,
                              }}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                    );
                  })
                )}
                <TableCell align="right">
                  <Button
                    size="small"
                    color="secondary"
                    disabled={pendingCell === `${project.id}:row`}
                    onClick={() => toggleRow(project)}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    {fullyOn ? 'None' : 'All'}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
